import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Radar, RefreshCw, CheckCircle2, XCircle } from "lucide-react";

const SOURCE_LABEL = {
  OFAC_SDN: "OFAC SDN (US Treasury)",
  EU: "EU Consolidated Sanctions",
  UN: "UN Security Council Consolidated",
  UK_HMT: "UK HMT / OFSI Consolidated",
  _seed: "Seed / fallback data",
  _fallback: "Network fallback (seed data)",
};

export default function SanctionsStatus() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/sanctions/status").then((r) => setMeta(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/sanctions/refresh");
      toast.success(`Refreshed — ${data.total.toLocaleString()} entries`);
      load();
    } catch (e) {
      toast.error("Refresh requires admin role");
    } finally {
      setLoading(false);
    }
  };

  if (!meta) return <div className="text-slate-500">Loading feeds…</div>;

  const sources = meta.sources || {};

  return (
    <div className="space-y-6" data-testid="sanctions-status-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl">
            Sanctions Feed Status
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Live public sanctions lists cached for fuzzy name matching.
          </p>
        </div>
        {(user?.role === "admin") && (
          <button
            data-testid="sanctions-refresh-btn"
            onClick={refresh}
            disabled={loading}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Refreshing…" : "Refresh Feeds"}
          </button>
        )}
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-300">
            <Radar className="h-4 w-4 text-blue-400" />
            <span className="font-mono text-xs uppercase tracking-wider">Total cached entries</span>
          </div>
          <span className="font-heading text-3xl font-extrabold tabular-nums text-blue-300">
            {(meta.total || 0).toLocaleString()}
          </span>
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-slate-800 pt-3">
          <span className="font-mono text-xs uppercase tracking-wider text-slate-400">
            Sanctioned crypto addresses (wallet provenance)
          </span>
          <span className="font-heading text-lg font-bold tabular-nums text-amber-300" data-testid="crypto-address-count">
            {(meta.crypto_addresses || 0).toLocaleString()}
          </span>
        </div>
        <div className="mt-1 font-mono text-[10px] text-slate-500">
          Last refreshed: {meta.refreshed_at || "—"}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Object.entries(sources).map(([key, v]) => (
          <div
            key={key}
            data-testid={`feed-${key}`}
            className="rounded-xl border border-slate-800 bg-slate-900/60 p-5"
          >
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Feed</span>
              {v.ok ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              ) : (
                <XCircle className="h-4 w-4 text-red-400" />
              )}
            </div>
            <div className="mt-2 font-heading text-sm font-semibold text-slate-100">
              {SOURCE_LABEL[key] || key}
            </div>
            <div className="mt-3 font-heading text-2xl font-extrabold tabular-nums text-slate-200">
              {(v.count || 0).toLocaleString()}
            </div>
            <div className="mt-1 text-xs text-slate-500">
              {v.ok ? "records loaded" : v.error || "unavailable"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
